import { Controller } from "@hotwired/stimulus";
import {
  computePosition,
  flip,
  shift,
  size,
  autoUpdate,
} from "@floating-ui/dom";

interface User {
  id: number;
  name: string;
  type?: string;
  group?: string;
  description?: string;
  option_html?: string;
}

interface MentionMatch {
  token: string;
  query: string;
  trigger: "@" | "/";
}

// Distance (px) a finger may travel between touchstart and touchend before
// the gesture is treated as a scroll rather than a tap.
const TAP_MOVEMENT_THRESHOLD_PX = 10;

export default class extends Controller {
  static targets = ["editor", "dropdown", "hiddenInput"];
  static values = {
    url: String,
    fieldName: String,
    initialContent: String,
    references: { type: Boolean, default: false },
    skillShortcut: { type: Boolean, default: false },
  };

  declare readonly editorTarget: HTMLDivElement;
  declare readonly dropdownTarget: HTMLElement;
  declare readonly hiddenInputTarget: HTMLInputElement;
  declare readonly hasDropdownTarget: boolean;
  declare readonly hasHiddenInputTarget: boolean;
  declare readonly urlValue: string;
  declare readonly referencesValue: boolean;
  declare readonly skillShortcutValue: boolean;
  declare readonly fieldNameValue: string;
  declare readonly initialContentValue: string;
  declare readonly hasInitialContentValue: boolean;

  private fetchVersion = 0;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private searchRequest: AbortController | null = null;
  private readonly inputHandler = this.handleInput.bind(this);
  private readonly keydownHandler = this.handleKeydown.bind(this);
  private readonly outsideHandler = this.handleClickOutside.bind(this);
  private users: User[] = [];
  private selectedIndex: number = -1;
  private mentionStart: number = -1;
  private dropdownElement: HTMLElement | null = null;
  private mentionQuery: string = "";
  private mentionTrigger: "@" | "/" = "@";

  // State machine properties for mention detection
  private isInMentionMode: boolean = false;
  private mentionStartNode: Node | null = null;
  private mentionStartOffset: number = -1;

  private touchStartPos: { x: number; y: number } | null = null;
  private stopAutoUpdate: (() => void) | null = null;

  connect() {
    this.editorTarget.addEventListener("input", this.inputHandler);
    this.editorTarget.addEventListener(
      "keydown",
      this.keydownHandler,
    );
    this.editorTarget.addEventListener("blur", this.handleEditorBlur);
    document.addEventListener("click", this.outsideHandler);
    this.createDropdown();

    // Load existing content if editing
    if (this.hasInitialContentValue && this.initialContentValue) {
      this.loadExistingContent(this.initialContentValue);
    }

    // Set placeholder behavior
    this.updatePlaceholder();
  }

  disconnect() {
    this.exitMentionMode();
    this.editorTarget.removeEventListener("input", this.inputHandler);
    this.editorTarget.removeEventListener(
      "keydown",
      this.keydownHandler,
    );
    this.editorTarget.removeEventListener("blur", this.handleEditorBlur);
    document.removeEventListener("click", this.outsideHandler);
    this.stopAutoUpdate?.();
    this.stopAutoUpdate = null;
    this.removeDropdown();
  }

  // When the editor loses focus and focus moves outside this composer
  // (e.g. the soft keyboard is dismissed on mobile, the user taps a
  // different field), close the dropdown. The small delay lets clicks/
  // touchends on dropdown items and toolbar buttons complete first —
  // those refocus the editor, so by the time the timeout runs the editor
  // is focused again and we leave the dropdown alone.
  private handleEditorBlur = () => {
    setTimeout(() => {
      if (this.element.contains(document.activeElement)) return;
      if (this.isInMentionMode) this.exitMentionMode();
    }, 150);
  };

  private createDropdown() {
    if (!this.hasDropdownTarget) {
      this.dropdownElement = document.createElement("div");
      this.dropdownElement.className =
        "mention-dropdown absolute bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50 hidden";
      this.dropdownElement.style.minWidth = "250px";
      const container = this.element.closest('[role="dialog"], [role="complementary"]') || document.body;
      container.appendChild(this.dropdownElement);
    } else {
      this.dropdownElement = this.dropdownTarget;
    }

    // Prevent editor from losing focus when clicking dropdown items (desktop).
    // We intentionally do NOT preventDefault on touchstart here: on mobile that
    // suppresses the synthetic click event (breaking item selection) and also
    // blocks scrolling inside the dropdown. Per-item touchend handles selection.
    this.dropdownElement.addEventListener("mousedown", (e) =>
      e.preventDefault(),
    );
  }

  private removeDropdown() {
    if (this.dropdownElement && !this.hasDropdownTarget) {
      this.dropdownElement.remove();
    }
  }

  private updatePlaceholder() {
    const isEmpty = this.editorTarget.textContent?.trim() === "";
    if (isEmpty) {
      this.editorTarget.classList.add("empty");
      // Clear any remaining HTML to ensure clean state
      this.editorTarget.innerHTML = "";
    } else {
      this.editorTarget.classList.remove("empty");
    }
  }

  private handleInput(event: Event) {
    this.updatePlaceholder();
    this.updateHiddenInput();

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const textBeforeCursor = this.getTextBeforeCursor(range);

    const match = this.matchMention(textBeforeCursor);

    if (match) {
      // Enter or continue mention mode
      if (!this.isInMentionMode || this.mentionTrigger !== match.trigger) {
        this.enterMentionMode(range, match);
      }

      this.mentionQuery = match.query.trim();

      // Exit mention mode if query is too long with no results
      if (this.mentionQuery.length > 50) {
        this.exitMentionMode();
        return;
      }

      this.search(this.mentionQuery);
    } else {
      // No reference trigger before the caret, exit mention mode
      if (this.isInMentionMode) {
        this.exitMentionMode();
      }
    }
  }

  private matchMention(text: string): MentionMatch | null {
    const match = this.skillShortcutValue
      ? text.match(/(?:^|\s)(\/)([^@/\n]*)$/) || text.match(/(@)([^@/\n]*)$/)
      : text.match(/(@)([^@\n]*)$/);
    return match ? { token: `${match[1]}${match[2]}`, query: match[2], trigger: match[1] as "@" | "/" } : null;
  }

  private enterMentionMode(range: Range, match: MentionMatch) {
    this.isInMentionMode = true;
    this.mentionTrigger = match.trigger;
    this.mentionStartNode = range.endContainer;
    this.mentionStartOffset = range.endOffset - match.token.length;
    this.mentionStart = this.mentionStartOffset;
  }

  private exitMentionMode() {
    this.cancelSearch();
    this.isInMentionMode = false;
    this.mentionStartNode = null;
    this.mentionStartOffset = -1;
    this.hideDropdown();
  }

  private getTextBeforeCursor(range: Range): string {
    const preRange = range.cloneRange();
    preRange.selectNodeContents(this.editorTarget);
    preRange.setEnd(range.endContainer, range.endOffset);

    // Extract text while ignoring content inside mention badges
    let text = "";
    const walker = document.createTreeWalker(
      preRange.commonAncestorContainer,
      NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
      {
        acceptNode: (node) => {
          // Skip text nodes inside mention badges
          if (node.nodeType === Node.TEXT_NODE) {
            let parent = node.parentElement;
            while (parent && parent !== this.editorTarget) {
              if (parent.classList?.contains("mention-badge")) {
                return NodeFilter.FILTER_REJECT;
              }
              parent = parent.parentElement;
            }
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      },
    );

    let currentNode;
    while ((currentNode = walker.nextNode())) {
      if (this.skillShortcutValue && currentNode instanceof HTMLElement &&
          ["BR", "DIV", "P"].includes(currentNode.tagName) && preRange.intersectsNode(currentNode)) {
        text += "\n";
      }
      if (currentNode.nodeType === Node.TEXT_NODE) {
        const textNode = currentNode as Text;

        // Check if this text node is within our range
        if (preRange.intersectsNode(textNode)) {
          let nodeText = textNode.textContent || "";

          // If this is the end node, only include text up to the end offset
          if (textNode === range.endContainer) {
            nodeText = nodeText.substring(0, range.endOffset);
          }

          text += nodeText;
        }
      }
    }

    return text;
  }

  private updateHiddenInput() {
    if (!this.hasHiddenInputTarget) return;

    // Extract HTML content from contenteditable, preserving mention badges
    const html = this.extractHtmlContent();
    this.hiddenInputTarget.value = html;
    this.dispatch("changed");
  }

  private extractHtmlContent(): string {
    return this.extractNodes(this.editorTarget.childNodes);
  }

  private extractNodes(nodes: NodeListOf<ChildNode>): string {
    let html = "";

    nodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        html += this.escapeHtml(node.textContent || "");
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        if (element.classList.contains("mention-badge")) {
          const identity = this.referencesValue
            ? `data-record-type="${this.escapeHtml(element.dataset.recordType || "")}" data-record-id="${this.escapeHtml(element.dataset.recordId || "")}"`
            : `data-user-id="${this.escapeHtml(element.dataset.userId || "")}"`;
          html += `<span class="mention-badge" ${identity}>${this.escapeHtml(element.textContent || "")}</span>`;
        } else if (element.tagName === "BR") {
          html += "<br>";
        } else if (element.tagName === "DIV" || element.tagName === "P") {
          html += "<br>";
          const isPlaceholder =
            element.childNodes.length === 1 &&
            (element.firstChild as HTMLElement)?.tagName === "BR";
          if (!isPlaceholder) {
            html += this.extractNodes(element.childNodes);
          }
        } else {
          html += this.extractNodes(element.childNodes);
        }
      }
    });

    return html;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  private loadExistingContent(htmlContent: string) {
    // Parse HTML content and insert into editor
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;

    // Clear existing content
    this.editorTarget.innerHTML = "";

    // Process nodes and insert into editor
    tempDiv.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        this.editorTarget.appendChild(node.cloneNode(true));
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;

        if (element.classList.contains("mention-badge")) {
          // Recreate mention badge
          const badge = document.createElement("span");
          badge.className = "mention-badge";
          badge.contentEditable = "false";
          if (this.referencesValue) {
            badge.dataset.recordType = element.dataset.recordType || "";
            badge.dataset.recordId = element.dataset.recordId || "";
          } else badge.dataset.userId = element.dataset.userId || "";
          badge.textContent = element.textContent || "";
          this.editorTarget.appendChild(badge);
        } else if (element.tagName === "BR") {
          this.editorTarget.appendChild(document.createElement("br"));
        } else if (element.tagName === "P") {
          // Handle paragraph - extract text and add line break
          const textContent = element.textContent || "";
          if (textContent) {
            this.editorTarget.appendChild(document.createTextNode(textContent));
          }
          // Add line break after paragraph (except for last one)
          if (element.nextSibling) {
            this.editorTarget.appendChild(document.createElement("br"));
          }
        } else {
          // For other elements, just get text
          this.editorTarget.appendChild(
            document.createTextNode(element.textContent || ""),
          );
        }
      }
    });
  }

  private handleKeydown(event: KeyboardEvent) {
    if (event.isComposing || event.keyCode === 229) return;
    if (this.referencesValue && this.handleReferenceEnter(event)) return;
    // Handle dropdown navigation
    if (
      this.dropdownElement &&
      !this.dropdownElement.classList.contains("hidden")
    ) {
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          this.selectedIndex = Math.min(
            this.selectedIndex + 1,
            this.users.length - 1,
          );
          this.updateSelection();
          break;
        case "ArrowUp":
          event.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
          this.updateSelection();
          break;
        case "Enter":
          if (this.selectedIndex >= 0 && this.users.length > 0) {
            event.preventDefault();
            this.selectUser(this.users[this.selectedIndex]);
          }
          break;
        case "Escape":
          event.preventDefault();
          this.exitMentionMode();
          break;
      }
    }
  }

  private handleReferenceEnter(event: KeyboardEvent): boolean {
    if (event.key !== "Enter") return false;
    if (event.shiftKey) {
      this.exitMentionMode();
      return true;
    }
    if (this.searchTimer !== null || this.searchRequest) {
      event.preventDefault();
      return true;
    }
    return false;
  }

  private handleClickOutside(event: Event) {
    if (
      this.dropdownElement &&
      !this.dropdownElement.contains(event.target as Node) &&
      !this.element.contains(event.target as Node)
    ) {
      this.exitMentionMode();
    }
  }

  private cancelSearch() {
    this.fetchVersion += 1;
    if (this.searchTimer !== null) clearTimeout(this.searchTimer);
    this.searchTimer = null;
    this.searchRequest?.abort();
    this.searchRequest = null;
  }

  private search(query: string) {
    this.cancelSearch();
    this.hideDropdown();
    if (this.referencesValue && query.length > 0) {
      this.searchTimer = setTimeout(() => this.fetchUsers(query), 150);
    } else {
      void this.fetchUsers(query);
    }
  }

  private async fetchUsers(query: string) {
    this.cancelSearch();
    const version = this.fetchVersion;
    this.searchRequest = new AbortController();
    try {
      const url = new URL(this.urlValue, window.location.origin);
      url.searchParams.append("q", query);
      if (this.skillShortcutValue) url.searchParams.set("kind", this.mentionTrigger === "/" ? "skills" : "records");

      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.searchRequest.signal,
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const users = await response.json();
      if (version !== this.fetchVersion || !this.isInMentionMode) return;
      this.users = users;
      this.displayUsers();
    } catch (error) {
      if (version !== this.fetchVersion) return;
      console.error("Error fetching users:", error);
      this.users = [];
      this.hideDropdown();
    } finally {
      if (version === this.fetchVersion) this.searchRequest = null;
    }
  }

  private displayUsers() {
    if (!this.dropdownElement) return;

    if (this.users.length === 0) {
      this.hideDropdown();
      return;
    }

    this.dropdownElement.innerHTML = "";
    this.selectedIndex = 0;

    this.users.forEach((user, index) => {
      if (this.referencesValue && user.group && (index === 0 || this.users[index - 1].group !== user.group)) {
        const heading = document.createElement("div");
        heading.className = "px-4 pt-3 pb-1 text-xs font-semibold text-base-content/60";
        heading.textContent = user.group;
        this.dropdownElement!.appendChild(heading);
      }
      const item = document.createElement("div");
      item.className =
        "mention-item px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm";

      if (user.option_html && !this.referencesValue) {
        item.innerHTML = user.option_html;
      } else {
        item.textContent = user.name;
        if (this.referencesValue && user.description) {
          const description = document.createElement("div");
          description.className = "mt-0.5 text-xs text-base-content/60";
          description.textContent = user.description;
          item.appendChild(description);
        }
      }

      if (index === this.selectedIndex) {
        item.classList.add("bg-gray-100");
      }

      item.addEventListener("click", () => {
        this.selectUser(user);
      });

      // On mobile, the click event is unreliable when the user has been
      // interacting with a contenteditable — touchend ensures the tap
      // registers. preventDefault stops the synthetic click from
      // double-firing selectUser. Compare touchstart vs touchend positions
      // so a scroll gesture doesn't accidentally select the item under
      // the finger.
      item.addEventListener("touchstart", (e) => {
        const t = (e as TouchEvent).touches?.[0];
        this.touchStartPos = t ? { x: t.clientX, y: t.clientY } : null;
      });

      item.addEventListener("touchend", (e) => {
        e.preventDefault();
        const start = this.touchStartPos;
        this.touchStartPos = null;
        if (start) {
          const t = (e as TouchEvent).changedTouches?.[0];
          if (t) {
            const dx = t.clientX - start.x;
            const dy = t.clientY - start.y;
            if (Math.hypot(dx, dy) > TAP_MOVEMENT_THRESHOLD_PX) {
              return;
            }
          }
        }
        this.selectUser(user);
      });

      this.dropdownElement!.appendChild(item);
    });

    this.dropdownElement.classList.remove("hidden");
    this.startPositioning();
  }

  private updateSelection() {
    if (!this.dropdownElement) return;

    const items = this.dropdownElement.querySelectorAll(".mention-item");
    items.forEach((item, index) => {
      if (index === this.selectedIndex) {
        item.classList.add("bg-gray-100");
        item.scrollIntoView({ block: "nearest" });
      } else {
        item.classList.remove("bg-gray-100");
      }
    });
  }

  private selectUser(user: User) {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);

    const textBeforeCursor = this.getTextBeforeCursor(range);
    const match = this.matchMention(textBeforeCursor);

    if (match) {
      // Replace only the trigger and query, preserving preceding whitespace.
      const deleteRange = range.cloneRange();
      deleteRange.setStart(
        range.endContainer,
        range.endOffset - match.token.length,
      );
      deleteRange.setEnd(range.endContainer, range.endOffset);
      deleteRange.deleteContents();

      // Create mention badge element with FULL NAME (including spaces)
      const badge = document.createElement("span");
      badge.className = "mention-badge";
      badge.contentEditable = "false"; // Make badge non-editable
      if (this.referencesValue) {
        badge.dataset.recordType = user.type || "";
        badge.dataset.recordId = user.id.toString();
      } else badge.dataset.userId = user.id.toString();
      const prefix = this.skillShortcutValue && user.type === "AssistantSkill" ? "/" : "@";
      badge.textContent = `${prefix}${user.name}`;

      // Insert badge and space
      const newRange = deleteRange.cloneRange();
      newRange.insertNode(badge);

      // Add space after badge
      const space = document.createTextNode(" ");
      badge.parentNode?.insertBefore(space, badge.nextSibling);

      // Move cursor after the space
      newRange.setStartAfter(space);
      newRange.setEndAfter(space);
      selection.removeAllRanges();
      selection.addRange(newRange);

      // Update hidden input
      this.updateHiddenInput();

      // Dispatch event for tracking mentions
      this.dispatch(this.referencesValue ? "reference-mentioned" : "user-mentioned", { detail: { user } });
    }

    // Exit mention mode after selecting
    this.exitMentionMode();
    this.editorTarget.focus();
  }

  // Floating UI replaces ~80 lines of manual position math and event
  // wiring. autoUpdate listens for scroll/resize/visualViewport changes
  // (including the iOS soft-keyboard offset that we previously corrected
  // by hand), and the middleware chain handles flipping, edge shifting,
  // height clamping, and mobile width matching.
  private startPositioning() {
    if (!this.dropdownElement) return;
    this.dropdownElement.style.position = "fixed";

    const reference = this.element as HTMLElement;
    const floating = this.dropdownElement;
    const isMobileViewport = () => window.innerWidth < 768;

    this.stopAutoUpdate?.();
    this.stopAutoUpdate = autoUpdate(reference, floating, () => {
      computePosition(reference, floating, {
        // Must match the element's actual position: fixed (set above).
        // floating-ui defaults to the absolute strategy, which yields
        // document coordinates — applied to a fixed element they render that
        // far down the *viewport*, pushing the dropdown off-screen whenever
        // document and viewport coordinates diverge (long page, or the
        // sticky support composer whose in-flow position is the page's end).
        strategy: "fixed",
        placement: "top-start",
        middleware: [
          flip({ fallbackPlacements: ["bottom-start"] }),
          shift({ padding: 8 }),
          size({
            padding: 8,
            apply({ availableHeight, rects }) {
              floating.style.maxHeight = `${Math.min(240, Math.max(0, availableHeight))}px`;
              if (isMobileViewport()) {
                floating.style.width = `${rects.reference.width}px`;
                floating.style.minWidth = "auto";
              } else {
                floating.style.width = "";
                floating.style.minWidth = "250px";
              }
            },
          }),
        ],
      }).then(({ x, y }) => {
        floating.style.left = `${x}px`;
        floating.style.top = `${y}px`;
      });
    });
  }

  private hideDropdown() {
    if (this.dropdownElement) {
      this.dropdownElement.classList.add("hidden");
    }
    this.stopAutoUpdate?.();
    this.stopAutoUpdate = null;
    this.users = [];
    this.selectedIndex = -1;
    this.mentionStart = -1;
  }


  // Public method to trigger mention from button
  triggerMention(event: Event) {
    event.preventDefault();

    // Focus the editor
    this.editorTarget.focus();

    // Get current selection
    const selection = window.getSelection();
    if (!selection) return;

    // If no selection, place cursor at the end
    if (selection.rangeCount === 0 || !this.editorTarget.contains(selection.getRangeAt(0).commonAncestorContainer)) {
      const range = document.createRange();
      range.selectNodeContents(this.editorTarget);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    // Insert @ symbol at cursor position
    const range = selection.getRangeAt(0);
    const atText = document.createTextNode("@");
    range.insertNode(atText);

    // Move cursor after the @
    range.setStart(atText, 1);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    // Set up mention state and trigger user search (updated regex)
    this.mentionQuery = "";
    const textBeforeCursor = this.getTextBeforeCursor(range);
    const match = this.matchMention(textBeforeCursor);

    if (match) {
      this.enterMentionMode(range, match);
      this.search(this.mentionQuery);
    }

    // Update placeholder and hidden input
    this.updatePlaceholder();
    this.updateHiddenInput();
  }
}
